import type { Tokens } from '@/auth/tokenStore'
import type { RefreshRejectionReason } from '@/features/auth/schemas/tokens'

export type AuthBroadcastMessage =
  | { type: 'tokens-updated'; tokens: Tokens }
  | { type: 'logout'; reason?: RefreshRejectionReason }

type Unsubscribe = () => void

const CHANNEL_NAME = 'auth-sync'

function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined') return null
  if (typeof BroadcastChannel === 'undefined') return null
  return new BroadcastChannel(CHANNEL_NAME)
}

export function postTokensUpdated(tokens: Tokens): void {
  const channel = getChannel()
  if (!channel) return
  try {
    channel.postMessage({ type: 'tokens-updated', tokens } satisfies AuthBroadcastMessage)
  } finally {
    channel.close()
  }
}

export function postLogout(reason?: RefreshRejectionReason): void {
  const channel = getChannel()
  if (!channel) return
  try {
    channel.postMessage({ type: 'logout', reason } satisfies AuthBroadcastMessage)
  } finally {
    channel.close()
  }
}

export function onTokensUpdated(cb: (tokens: Tokens) => void): Unsubscribe {
  const channel = getChannel()
  if (!channel) return () => {}
  const listener = (event: MessageEvent<AuthBroadcastMessage>) => {
    if (event.data?.type === 'tokens-updated') cb(event.data.tokens)
  }
  channel.addEventListener('message', listener)
  return () => {
    channel.removeEventListener('message', listener)
    channel.close()
  }
}

export function onLogout(cb: (reason?: RefreshRejectionReason) => void): Unsubscribe {
  const channel = getChannel()
  if (!channel) return () => {}
  const listener = (event: MessageEvent<AuthBroadcastMessage>) => {
    if (event.data?.type === 'logout') cb(event.data.reason)
  }
  channel.addEventListener('message', listener)
  return () => {
    channel.removeEventListener('message', listener)
    channel.close()
  }
}
